import {
  PhoneIncoming, PhoneOutgoing, MessageSquare, Languages, BarChart3, Sparkles,
  Mic, FileText, Gauge, Radio, BookOpen, UserCheck, Clock, Webhook,
  Boxes, Network, ListTree, Users, Hash, PhoneCall, MonitorSmartphone, ShieldCheck,
  Building2, KeyRound, Lock, ServerCog, Layers, Repeat, Globe, Zap, LayoutDashboard,
  Handshake, PaintBucket, Share2, TrendingUp, Headset, MousePointerClick,
} from "lucide-react";

// visual: "waveform" | "chat" | "list"
export const pages = {
  "ai/overview": {
    category: "AI Voice Agent",
    visual: "waveform",
    title: "Your AI voice agent, end to end.",
    lead: "Answer, speak, analyze and hand off — a single agent that lives inside your phone network and works like a real teammate.",
    cards: [
      { icon: PhoneIncoming, title: "Inbound answering", desc: "Assign any number to an AI agent that picks up instantly." },
      { icon: PhoneOutgoing, title: "Outbound calling", desc: "Places calls on your own caller ID for follow-ups and outreach." },
      { icon: MessageSquare, title: "Web chat & widget", desc: "The same brain, live on your website as chat." },
      { icon: Languages, title: "Multilingual", desc: "Fluent in English, Hindi and natural Hinglish." },
      { icon: BarChart3, title: "Analytics & insights", desc: "Sentiment, intent and topics on every call." },
      { icon: UserCheck, title: "Human hand-off", desc: "Transfers a live call to a person when it matters." },
      { icon: ListTree, title: "Escalation rules", desc: "Configurable rules decide exactly when a call goes to a human." },
      { icon: MessageSquare, title: "SMS follow-up", desc: "Automatic text follow-up after a call, no extra setup." },
    ],
    bullets: [
      "Real-time two-way conversations with natural barge-in", "Inbound AI answering on any number",
      "Outbound AI calling on your caller ID", "Web chat & website widget",
      "English / Hindi / Hinglish", "Automatic recording & transcription",
      "AI sentiment & intent analysis", "Live call monitoring", "Performance analytics dashboard",
      "Custom knowledge base per agent", "Smart human hand-off", "Configurable escalation rules",
      "SMS follow-up after calls", "Unanswered-questions log", "Business-hours aware messages",
      "Webhook / CRM integration on completion",
    ],
  },
  "ai/inbound": {
    category: "AI Voice Agent",
    visual: "waveform",
    title: "Never miss an inbound call again.",
    lead: "Point any business number at an AI agent and it answers instantly — on brand, on message, 24/7.",
    cards: [
      { icon: Zap, title: "Instant pickup", desc: "No hold music, no missed calls — the agent answers on the first ring." },
      { icon: BookOpen, title: "Knows your business", desc: "Answers from a knowledge base you control, per agent." },
      { icon: Mic, title: "Natural barge-in", desc: "Handles interruptions gracefully — never talks over the caller." },
      { icon: Clock, title: "Business-hours aware", desc: "Plays the right message inside and outside working hours." },
      { icon: UserCheck, title: "Smart hand-off", desc: "Escalates to a human agent the moment it's needed." },
      { icon: FileText, title: "Recorded & transcribed", desc: "Every inbound call logged turn-by-turn, automatically." },
    ],
    bullets: [
      "Assign any DID to an AI agent", "First-ring answering", "Per-agent knowledge base",
      "Barge-in interruption handling", "Business-hours routing", "Live monitoring",
      "Auto recording & transcript", "Sentiment & intent scoring", "Human transfer", "CRM webhook on completion",
    ],
  },
  "ai/outbound": {
    category: "AI Voice Agent",
    visual: "waveform",
    title: "Outbound calls that sound personal.",
    lead: "The agent places calls on your own number — follow-ups, reminders and outreach — at a scale a human team can't match.",
    cards: [
      { icon: PhoneOutgoing, title: "Your real caller ID", desc: "Calls go out on the business's own number, not a generic one." },
      { icon: Repeat, title: "Follow-ups & reminders", desc: "Automate the calls your team never gets around to." },
      { icon: TrendingUp, title: "Outreach campaigns", desc: "Run personal-sounding outreach without hiring." },
      { icon: Radio, title: "Live monitoring", desc: "Supervisors can listen in as calls happen." },
      { icon: Webhook, title: "CRM sync", desc: "Outcome and transcript pushed the moment a call ends." },
      { icon: Gauge, title: "Sentiment scoring", desc: "Know how every outbound conversation landed." },
    ],
    bullets: [
      "Outbound on your own caller ID", "Automated follow-ups & reminders", "Personal-sounding outreach",
      "Barge-in handling", "Live monitoring", "Recording & transcription", "Sentiment & intent analysis",
      "CRM / webhook delivery", "Per-agent knowledge base",
    ],
  },
  "ai/web-chat": {
    category: "AI Voice Agent",
    visual: "chat",
    showCallWidget: true,
    title: "The same AI agent, now on your website.",
    lead: "A live web chat and embeddable widget powered by the very same brain that answers your phone calls.",
    cards: [
      { icon: MessageSquare, title: "Website widget", desc: "Drop-in chat widget for any site — matches your brand." },
      { icon: Layers, title: "Unified knowledge", desc: "Chat and voice share one knowledge base and personality." },
      { icon: PhoneCall, title: "Chat-to-call escalation", desc: "Hand a chat off to a live voice call seamlessly." },
      { icon: Languages, title: "Multilingual chat", desc: "Replies in English, Hindi or Hinglish." },
      { icon: FileText, title: "Transcripts & analytics", desc: "Every chat logged, scored and searchable." },
      { icon: Webhook, title: "CRM webhook", desc: "Conversation outcome pushed to your systems." },
    ],
    bullets: [
      "Embeddable website widget", "Shared knowledge with voice agent", "Chat-to-call escalation",
      "Multilingual replies", "Automatic transcripts", "Sentiment & intent", "Human hand-off", "CRM integration",
    ],
  },
  "ai/multilingual": {
    category: "AI Voice Agent",
    visual: "waveform",
    title: "Fluent in English, Hindi & Hinglish — and 10+ Indian languages.",
    lead: "Natural mixed-language conversation with correct grammar matched to the voice — and growing coverage across 10+ Indian languages. A genuine differentiator for Indian markets.",
    languages: [
      "English", "हिन्दी Hindi", "Hinglish", "தமிழ் Tamil", "తెలుగు Telugu", "বাংলা Bengali",
      "मराठी Marathi", "ગુજરાતી Gujarati", "ಕನ್ನಡ Kannada", "മലയാളം Malayalam",
      "ਪੰਜਾਬੀ Punjabi", "ଓଡ଼ିଆ Odia", "اردو Urdu", "অসমীয়া Assamese",
    ],
    cards: [
      { icon: Globe, title: "10+ Indian languages", desc: "Beyond English & Hindi — Tamil, Telugu, Bengali, Marathi and more." },
      { icon: Languages, title: "Hindi & Hinglish", desc: "Fluent Hindi and the natural Hinglish customers actually speak." },
      { icon: Sparkles, title: "English", desc: "Clear, professional English for global callers." },
      { icon: Mic, title: "Voice-matched grammar", desc: "Grammar and tone matched to the chosen voice." },
      { icon: Repeat, title: "In-call switching", desc: "Moves between languages as the caller does." },
      { icon: Building2, title: "Local-market ready", desc: "Built for India's multilingual businesses." },
    ],
    bullets: [
      "Covers 10+ Indian languages", "English support", "Hindi support", "Natural Hinglish",
      "Voice-matched grammar", "Mid-conversation language switching", "Consistent across voice & chat",
    ],
  },
  "ai/analytics": {
    category: "AI Voice Agent",
    visual: "list",
    reporting: "ai",
    title: "Every call, measured.",
    lead: "Sentiment, intent, topics and outcomes — scored automatically on every conversation, no manual review.",
    cards: [
      { icon: Gauge, title: "Sentiment analysis", desc: "How the caller felt, scored per call." },
      { icon: BarChart3, title: "Intent detection", desc: "Support, sales, billing — detected automatically." },
      { icon: ListTree, title: "Topic clustering", desc: "See the themes callers raise most." },
      { icon: Users, title: "Per-agent performance", desc: "Success rate and trends for every agent." },
      { icon: TrendingUp, title: "Daily volume trends", desc: "Calls per day and how they change over time." },
      { icon: FileText, title: "Exportable reports", desc: "Pull the numbers into your own tools." },
      { icon: LayoutDashboard, title: "Live agent wallboard", desc: "Who's on a call, who's free, right now — same view your team uses." },
      { icon: MessageSquare, title: "Unanswered-questions log", desc: "See exactly what the AI couldn't answer, so you can fix it." },
    ],
    bullets: [
      "Per-call sentiment", "Intent classification", "Topic clustering", "Per-agent dashboards",
      "Live agent wallboard", "Calls-per-day trends", "Success-rate tracking", "Unanswered-questions log",
      "Full transcripts", "Export & webhooks",
    ],
  },
  "pbx/multi-tenant": {
    category: "PBX Platform",
    visual: "pbx",
    pbxKind: "multitenant",
    title: "One platform. Many isolated businesses.",
    lead: "True multi-tenant architecture — every account fully isolated with its own numbers, users, routing and settings.",
    cards: [
      { icon: Boxes, title: "Isolated tenants", anim: "isolate", detail: "Every business you onboard runs in its own walled-off space. Numbers, extensions, users, recordings and settings never cross between tenants — so one client can never see, hear or affect another's calls." },
      { icon: LayoutDashboard, title: "Per-tenant config", anim: "toggles", detail: "Each tenant configures its own extensions, IVR menus, call routing, ring groups and business hours independently. Change one tenant's flow without touching anyone else's setup." },
      { icon: ServerCog, title: "Central administration", anim: "console", detail: "Run your entire estate from a single control plane. Provision accounts, assign numbers, set limits and monitor activity across every tenant from one dashboard." },
      { icon: Network, title: "BYOC billing", anim: "connect", detail: "Bring your own carrier. Connect the SIP trunks and rates you already have, keep full control of call costs, and never get locked into a single provider." },
      { icon: Handshake, title: "Reseller-ready", anim: "rebrand", detail: "Spin up new tenants for your own clients in minutes and run them under your brand. Perfect for agencies and MSPs who want to sell phone service without building infrastructure." },
      { icon: TrendingUp, title: "Scales with you", anim: "scale", detail: "Start with five extensions or run thousands across hundreds of tenants. The platform grows with you — no re-platforming when you land a bigger client." },
    ],
    bullets: [
      "Isolated business accounts", "Per-tenant numbers & users", "Independent routing & IVR",
      "Central admin console", "Bring-your-own-carrier billing", "Reseller support", "Role-based access",
    ],
  },
  "pbx/features": {
    category: "PBX Platform",
    visual: "pbx",
    pbxKind: "callflow",
    title: "A complete, carrier-grade phone system.",
    lead: "Everything a modern business phone system needs — from extensions and IVR to queues, DIDs and recording.",
    cards: [
      { icon: Network, title: "Extensions & trunks", anim: "extensions", detail: "Create and manage internal extensions for every user and connect SIP trunks to the outside world. Assign, move and reconfigure extensions on the fly, no downtime." },
      { icon: ListTree, title: "IVR menus", anim: "ivr", detail: "Build 'Press 1 for Sales, 2 for Support' menus that route callers exactly where they need to go — with nested options, fallbacks and time-based branches." },
      { icon: Users, title: "Ring groups & queues", anim: "queue", detail: "Distribute incoming calls across teams with ring groups, and hold callers in smart queues so the next available agent always picks up." },
      { icon: Hash, title: "DID management", anim: "did", detail: "Provision, assign and manage direct-inward-dialing numbers. Point any DID at an extension, IVR, queue or AI agent in a couple of clicks." },
      { icon: PhoneCall, title: "Call recording", anim: "recording", detail: "Automatically record calls for quality and compliance, with per-tenant policies for what's captured and how long it's retained." },
      { icon: Clock, title: "Business hours", anim: "hours", detail: "Route calls differently inside and outside working hours — play the right greeting, send to voicemail or an after-hours flow automatically." },
    ],
    bullets: [
      "Extensions & trunk management", "IVR menus", "Ring groups & call queues", "DID / number management",
      "Business hours rules", "Call recording", "Live call view for admins", "Reporting dashboards",
    ],
  },
  "pbx/softphone": {
    category: "PBX Platform",
    visual: "pbx",
    pbxKind: "softphone",
    title: "Softphone & agent portal.",
    lead: "Make and receive calls from any browser — plus a dedicated portal purpose-built for call-center staff.",
    cards: [
      { icon: MonitorSmartphone, title: "Browser softphone", anim: "softphone", detail: "Make and receive calls straight from any modern browser — no desk phone, no downloads. Log in and start dialing from anywhere." },
      { icon: Headset, title: "Agent portal", anim: "console", detail: "A focused workspace for call-center staff with the queues, contacts and controls they need — and nothing they don't." },
      { icon: Users, title: "Queue & presence", anim: "presence", detail: "See who's available, busy or away in real time, and watch live queues so supervisors always know where calls are waiting." },
      { icon: MousePointerClick, title: "Click-to-call", anim: "clicktocall", detail: "Dial any number with a single click from the browser — no manual keying, fewer misdials and faster outbound." },
      { icon: PhoneCall, title: "Full call controls", anim: "controls", detail: "Hold, transfer, record and hang up mid-call with a click. Everything an agent needs to handle a live conversation." },
      { icon: LayoutDashboard, title: "Live wallboard", anim: "dashboard", detail: "The same team wallboard your supervisors use day to day — who's on a call, who's free, active calls and queue depth, updated in real time." },
    ],
    bullets: [
      "Browser-based softphone", "No desk phone required", "Dedicated agent portal", "Queue & presence view",
      "Click-to-call", "Hold / transfer / record controls", "Live team wallboard",
    ],
  },
  "pbx/security": {
    category: "PBX Platform",
    visual: "pbx",
    pbxKind: "security",
    title: "Security & reliability, built in.",
    lead: "IP whitelisting, login protection and role-based access — on infrastructure you actually own and control.",
    cards: [
      { icon: ShieldCheck, title: "IP whitelisting", anim: "whitelist", detail: "Restrict access to trusted networks only. Requests from unknown IPs are blocked before they ever reach your PBX." },
      { icon: Lock, title: "Login protection", anim: "lock", detail: "Hardened authentication with brute-force protection stops repeated failed logins and credential-stuffing attempts cold." },
      { icon: KeyRound, title: "Role-based access", anim: "roles", detail: "Give admins, agents and viewers exactly the permissions they need — nothing more — with granular, tenant-aware roles." },
      { icon: ServerCog, title: "Owned infrastructure", anim: "rack", detail: "Runs on real, self-owned telephony infrastructure — not a wrapper around someone else's API — for full control over routing and recording." },
      { icon: FileText, title: "Data ownership", anim: "isolate", detail: "Your call data, recordings and transcripts stay on infrastructure you control. No dependence on a third-party calling platform." },
      { icon: Radio, title: "Recording controls", anim: "toggles-rec", detail: "Policy-based recording and retention — decide what's recorded, who can access it and how long it's kept, per tenant." },
    ],
    bullets: [
      "IP whitelisting", "Login / brute-force protection", "Role-based admin access", "Tenant-level user management",
      "Self-hosted data ownership", "Owned carrier-grade infrastructure", "Recording & retention controls",
    ],
  },
  "partners/reseller": {
    category: "Partners",
    visual: "list",
    title: "Resell OwnPBX under your own brand.",
    lead: "Sell a modern phone system with AI built in to your own clients — with the margins and control a reseller needs.",
    cards: [
      { icon: Handshake, title: "Your accounts", desc: "Create and manage client tenants yourself." },
      { icon: Network, title: "Bring your carrier", desc: "BYOC billing keeps you in control of costs." },
      { icon: TrendingUp, title: "Healthy margins", desc: "Transparent wholesale pricing to resell on." },
      { icon: LayoutDashboard, title: "Central control", desc: "One console for every client you run." },
      { icon: ShieldCheck, title: "Isolated & secure", desc: "Each client fully walled off from the rest." },
      { icon: Headset, title: "Partner support", desc: "We back you up behind the scenes." },
    ],
    bullets: [
      "Manage your own client tenants", "Bring-your-own-carrier billing", "Wholesale reseller pricing",
      "Central multi-tenant console", "Full account isolation", "Partner support",
    ],
  },
  "partners/white-label": {
    category: "Partners",
    visual: "list",
    title: "Make the whole platform yours.",
    lead: "Fully rebrand OwnPBX — your name, your logo, your colors — and offer a complete AI phone platform as your own product.",
    cards: [
      { icon: PaintBucket, title: "Full rebrand", desc: "Your brand across the entire product." },
      { icon: Globe, title: "Your domain", desc: "Run the platform on your own domain." },
      { icon: Layers, title: "Complete feature set", desc: "AI agent + full PBX, under your name." },
      { icon: ServerCog, title: "Dedicated options", desc: "Dedicated infrastructure for larger partners." },
      { icon: Handshake, title: "Client tenants", desc: "Onboard and manage your own customers." },
      { icon: ShieldCheck, title: "Enterprise SLA", desc: "Backed by an SLA for peace of mind." },
    ],
    bullets: [
      "Full visual rebrand", "Custom domain", "AI Voice Agent + full PBX", "Dedicated infrastructure option",
      "Client tenant management", "Custom AI training", "Enterprise SLA",
    ],
  },
  "partners/affiliate": {
    category: "Partners",
    visual: "list",
    title: "Refer, co-sell, earn.",
    lead: "Introduce OwnPBX to businesses that need it and earn on every referral — or co-sell alongside our team.",
    cards: [
      { icon: Share2, title: "Referral rewards", desc: "Earn on the deals you send our way." },
      { icon: Handshake, title: "Co-sell support", desc: "We join you on calls to close together." },
      { icon: BarChart3, title: "Transparent tracking", desc: "See your referrals and payouts clearly." },
      { icon: BookOpen, title: "Enablement material", desc: "Decks and demos to help you pitch." },
      { icon: Zap, title: "Fast onboarding", desc: "Start referring in minutes." },
      { icon: TrendingUp, title: "Recurring upside", desc: "Ongoing rewards, not one-off." },
    ],
    bullets: [
      "Referral commissions", "Co-sell opportunities", "Referral & payout tracking",
      "Sales enablement material", "Quick onboarding", "Recurring rewards",
    ],
  },
};
